import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as api from "../api/client";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs } from "../logic/inert";

/**
 * Landing screen after register, convert, or a login attempt that
 * was rejected with `email_not_verified`. The user can't log in
 * until they click the link in the email we just sent; this page
 * tells them so and lets them request a fresh one.
 */
export function CheckYourEmailPage() {
  const theme = pixelTheme;
  const [params] = useSearchParams();
  // The URL may or may not carry the email — register/convert do
  // (we just collected it), the login redirect doesn't (the SPA
  // didn't keep it around). Either way the form below lets the
  // user type or confirm an address and resend.
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function onResend(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || resending) return;
    setResending(true);
    setResent(false);
    try {
      await api.resendVerification(trimmed);
      setResent(true);
    } catch {
      // The server always responds 200 (no enumeration), so we
      // only land here on transport errors — swallow and let the
      // user retry.
    } finally {
      setResending(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onResend}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          CHECK YOUR EMAIL
        </div>
        <div style={{ fontSize: 14, color: theme.inkDim, lineHeight: 1.5 }}>
          Click the verification link we sent you. You can't log in
          until you do. Didn't get it? Type your email and we'll send
          another.
        </div>
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          EMAIL
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setResent(false);
            }}
            disabled={resending}
            placeholder="you@example.com"
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        <button
          type="submit"
          {...inertAttrs(resending || resent || email.trim() === "")}
          {...withBtnStyle(defaultBtn(theme, resending || resent), {
            fontSize: 12,
            padding: "10px 16px",
          })}
        >
          {resent ? "SENT — CHECK YOUR INBOX" : resending ? "…" : "RESEND EMAIL"}
        </button>
        {import.meta.env.DEV && (
          <a
            href="/dev/mailbox"
            target="_blank"
            rel="noreferrer"
            style={{
              textAlign: "center",
              fontSize: 11,
              color: theme.inkFaint,
              letterSpacing: theme.letterSpacing,
            }}
          >
            (dev) open mailbox →
          </a>
        )}
        <Link
          to="/login"
          style={{ textAlign: "center", fontSize: 12, color: theme.inkDim }}
        >
          Back to login
        </Link>
      </form>
    </div>
  );
}
